/**
 * Demo-data render of the real DashboardPage — no auth, no Appwrite.
 * Used only at /preview/dashboard (embedded as an iframe on the marketing site).
 */
import {
  Target, Clock, TrendingUp, CheckCircle2, Flame,
  AlertTriangle, ChevronRight, ArrowRight, Search,
} from "lucide-react";
import { SUBJECTS_DATA } from "@/data/subjects";

const DEMO = {
  name: "Emma",
  totalAttempted: 247,
  totalCorrect: 198,
  accuracy: 80.2,
  totalTimeSpent: 15120,
  lastTopicId: "algebra",
  lastSubjectId: "sat",
  lastModuleId: "math",
  weakTopics: [
    { id: "standard_english", label: "Standard English Conventions", acc: 55 },
    { id: "problem_solving",  label: "Problem Solving & Data",       acc: 57 },
    { id: "craft_structure",  label: "Craft & Structure",             acc: 68 },
  ],
  statsBySubject: {
    sat:          { attempted: 183, correct: 152, accuracy: 83 },
    act:          { attempted: 64,  correct: 46,  accuracy: 72 },
  },
  statsByTopic: {
    algebra:          { attempted: 48, correct: 44, accuracy: 92 },
    advanced_math:    { attempted: 27, correct: 20, accuracy: 74 },
    problem_solving:  { attempted: 35, correct: 20, accuracy: 57 },
    info_ideas:       { attempted: 41, correct: 35, accuracy: 85 },
    standard_english: { attempted: 33, correct: 18, accuracy: 55 },
    craft_structure:  { attempted: 22, correct: 15, accuracy: 68 },
  },
};

function formatTime(sec: number) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function PreviewDashboardPage() {
  const statsBySubject = DEMO.statsBySubject as Record<string, { attempted: number; correct: number; accuracy: number }>;

  return (
    <div className="min-h-screen bg-[#f8f9fc] flex flex-col" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Header */}
      <div className="bg-white border-b border-[#dde3ed] shadow-sm">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-6">
          <img src="/logo.svg" alt="Scholar Sphere" className="h-10 w-auto" />
          <div className="hidden md:flex items-center gap-1">
            {["Dashboard", "Question Banks"].map((l, i) => (
              <div key={l} className={`px-4 py-2 rounded-lg text-sm font-semibold ${i === 0 ? "bg-[#1a3a6b]/10 text-[#1a3a6b]" : "text-[#5a6a7e]"}`}>{l}</div>
            ))}
          </div>
          <div className="w-8 h-8 rounded-full bg-[#1a3a6b] flex items-center justify-center text-[#f5a623] text-xs font-bold flex-shrink-0">
            {DEMO.name.slice(0, 1)}
          </div>
        </div>
      </div>

      {/* Hero banner */}
      <div className="bg-gradient-to-r from-[#070f24] via-[#0d2248] to-[#1a3a6b]">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-[#f5a623] text-sm font-semibold tracking-wide uppercase mb-2">Your Dashboard</p>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-1">
                Welcome back, {DEMO.name}! 👋
              </h1>
              <p className="text-white/60 text-sm">Here's an overview of your learning progress.</p>
            </div>
            <div className="flex gap-6">
              {[
                { n: DEMO.totalAttempted, l: "Questions" },
                { n: `${DEMO.accuracy.toFixed(0)}%`, l: "Accuracy" },
                { n: formatTime(DEMO.totalTimeSpent), l: "Practiced" },
              ].map((s, i) => (
                <>
                  {i > 0 && <div key={`div-${i}`} className="w-px bg-white/10" />}
                  <div key={s.l} className="text-center">
                    <div className="text-3xl font-black text-[#f5a623]">{s.n}</div>
                    <div className="text-white/60 text-xs mt-0.5">{s.l}</div>
                  </div>
                </>
              ))}
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-8">

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Questions Attempted", value: DEMO.totalAttempted,            Icon: Target,       color: "bg-blue-50 text-blue-600" },
            { label: "Correct Answers",     value: DEMO.totalCorrect,              Icon: CheckCircle2, color: "bg-green-50 text-green-600" },
            { label: "Overall Accuracy",    value: `${DEMO.accuracy.toFixed(1)}%`, Icon: TrendingUp,   color: "bg-purple-50 text-purple-600" },
            { label: "Time Practiced",      value: formatTime(DEMO.totalTimeSpent),Icon: Clock,        color: "bg-amber-50 text-amber-600" },
          ].map(({ label, value, Icon, color }) => (
            <div key={label} className="bg-white border border-[#dde3ed] rounded-2xl p-5 shadow-sm">
              <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
                <Icon className="w-5 h-5" />
              </div>
              <p className="text-2xl font-extrabold text-[#1a3a6b] leading-none mb-1">{value}</p>
              <p className="text-xs text-[#5a6a7e] font-medium">{label}</p>
            </div>
          ))}
        </div>

        {/* Two cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-10">
          {/* Continue practicing */}
          <div className="bg-white border border-[#dde3ed] rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-[#f5a623]" />
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-[#1a3a6b]/10 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-[#1a3a6b]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1a2435]">Continue Practicing</h3>
                  <p className="text-xs text-[#5a6a7e]">Pick up where you left off</p>
                </div>
              </div>
              <p className="text-sm text-[#5a6a7e] mb-4">
                Last practiced: <span className="text-[#1a2435] font-semibold">SAT Math — Algebra</span>
              </p>
              <div className="w-full flex items-center justify-center gap-2 bg-[#1a3a6b] text-white font-semibold py-2.5 px-4 rounded-xl text-sm cursor-default">
                Resume Practice <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Focus areas */}
          <div className="bg-white border border-[#dde3ed] rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-orange-400" />
            <div className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-xl bg-orange-50 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1a2435]">Focus Areas</h3>
                  <p className="text-xs text-[#5a6a7e]">Topics that need more practice</p>
                </div>
              </div>
              {DEMO.weakTopics.map(t => (
                <div key={t.id} className="flex items-center justify-between py-2 border-b border-[#dde3ed] last:border-0">
                  <span className="text-sm text-[#1a2435]">{t.label}</span>
                  <span className="text-xs font-semibold text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full">{t.acc}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Subject banks */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-xl font-bold text-[#1a2435]">Question Banks</h2>
              <p className="text-sm text-[#5a6a7e]">Select a subject to start practicing</p>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6a7e]" />
              <div className="pl-9 pr-4 py-2 text-sm rounded-xl border border-[#dde3ed] bg-white w-52 text-[#5a6a7e]">
                Search subjects…
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SUBJECTS_DATA.map(subject => {
              const totalQ = subject.modules.reduce(
                (acc, m) => acc + m.topics.reduce((a, t) => a + (t.questionCount || 0), 0), 0
              );
              const subStat = statsBySubject[subject.id];
              const isComingSoon = subject.comingSoon;
              return (
                <div key={subject.id} className={`bg-white border border-[#dde3ed] rounded-2xl overflow-hidden shadow-sm transition-shadow ${isComingSoon ? "opacity-70" : "hover:shadow-md"}`}>
                  <div className={`h-2 bg-gradient-to-r ${subject.gradient}`} />
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className={`font-bold text-base leading-tight ${isComingSoon ? "text-[#5a6a7e]" : "text-[#1a2435]"}`}>{subject.title}</h3>
                      {isComingSoon ? (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                          Coming Soon
                        </span>
                      ) : totalQ > 0 ? (
                        <span className="text-xs font-semibold text-[#1a3a6b] bg-[#1a3a6b]/10 px-2 py-0.5 rounded-full whitespace-nowrap ml-2">
                          {totalQ.toLocaleString()}+ Qs
                        </span>
                      ) : null}
                    </div>
                    {isComingSoon ? (
                      <p className="text-xs text-[#5a6a7e] mb-3 line-clamp-2">Question bank coming soon. Tutoring available now.</p>
                    ) : subStat ? (
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1.5">
                          <span className="text-[#5a6a7e]">Accuracy</span>
                          <span className="font-bold" style={{ color: subStat.accuracy >= 75 ? '#22c55e' : '#f59e0b' }}>
                            {subStat.accuracy.toFixed(0)}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-[#eef1f6] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all"
                            style={{ width: `${subStat.accuracy}%`, background: subStat.accuracy >= 75 ? '#22c55e' : '#f59e0b' }} />
                        </div>
                        <p className="text-xs text-[#5a6a7e] mt-1">{subStat.attempted} questions practiced</p>
                      </div>
                    ) : (
                      <p className="text-xs text-[#5a6a7e] mb-3 line-clamp-2">{subject.description}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#5a6a7e]">
                        {subject.modules.length} module{subject.modules.length !== 1 ? "s" : ""}
                      </span>
                      {isComingSoon ? (
                        <span className="text-xs font-semibold text-[#5a6a7e]">Coming soon</span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs font-semibold text-[#f5a623]">
                          Practice <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
